import * as vp from 'vopay';
import { FunctionComponent, JSX, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';

const { Table, Box, SubTitle, Badge, Button, formattedCurrency, Flag } = vp;

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  clientAccountId: string;
};

type TransactionInfoProps = {
  transaction: vp.GlobalSendRecentTransaction;
  notes: string;
};

type KeyValuePair = {
  label: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type FC = FunctionComponent<TransactionInfoProps>;

const CrossBorderWithdrawalInfo: FC = ({ transaction, notes }: TransactionInfoProps) => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CrossBorderWithdrawalInfo',
  });
  const { t: tTransactionHistory } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory' });

  const transactionDetailsProps = useMemo<vp.TableProps>(
    () => ({
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        label: { header: t('TransactionDetails.header') || '' },
        value: { header: '' },
      },
      rows: [
        {
          label: t('TransactionDetails.transactionID'),
          value: transaction.TransactionID,
        },
        {
          label: t('TransactionDetails.transactionStatus'),
          value: (
            <Badge
              label={tTransactionHistory(`statuses.${transaction.Status}`, {
                defaultValue: transaction.Status,
              })}
              hasIcon
              color={vp.GetColorStatus(transaction.Status)}
            />
          ),
        },
        {
          label: t('TransactionDetails.transactionDate'),
          value: transaction.TransactionDate,
        },
        {
          label: t('TransactionDetails.type'),
          value: t('withdraw'),
        },
        {
          label: t('TransactionDetails.totalAmount'),
          value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.Amount)}`,
        },
        {
          label: t('TransactionDetails.principalAmount'),
          value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.PrincipalAmount)}`,
        },
        {
          label: t('TransactionDetails.fees'),
          value: `${transaction.SenderCurrency} ${formattedCurrency(
            String(Number(transaction.Amount) - Number(transaction.PrincipalAmount)),
          )}`,
        },
        {
          label: t('TransactionDetails.customerCreditedAmount'),
          value: `${transaction.RecipientCurrency} ${formattedCurrency(transaction.CustomerCreditedAmount)}`,
        },
        {
          label: t('TransactionDetails.fxRate'),
          value: transaction.FXRate,
        },
        {
          label: t('TransactionDetails.destinationCountry'),
          value: (
            <Box flx="row" ai="center" gapSize="5px">
              {transaction.DestinationCountryAbbreviation}{' '}
              <Flag flag={transaction.DestinationCountryAbbreviation} width="24px" />
            </Box>
          ),
        },
        {
          label: t('TransactionDetails.deliveryMethod'),
          value: transaction.DeliveryMethodType,
        },
        {
          label: t('TransactionDetails.notes'),
          value: notes,
        },
      ],
    }),
    [transaction, notes],
  );

  const senderDetailsProps = useMemo(() => {
    const senderDetailsPairs: KeyValuePair[] = [
      {
        label: t('SenderDetails.name'),
        value: (
          <Box jc="fs" ai="center" gap="sm">
            <Box>{`${transaction.SenderFirstName} ${transaction.SenderLastName}`}</Box>
            {clientAccountsEnabled && state && state.clientAccountId && state.clientAccountId !== '' && (
              <Button
                id="view-client-account-details"
                label={t('SenderDetails.view')}
                width="50px"
                height="30px"
                color="secondary"
                onClick={() => {
                  history.push(`/client-accounts/${state.clientAccountId}`);
                  onSubNavClick(
                    'clientAccountsList',
                    `/client-accounts/${state.clientAccountId}`,
                    t('SenderDetails.clientAccountsList'),
                  );
                }}
              />
            )}
          </Box>
        ),
        visible: !!transaction.SenderFirstName,
      },
      {
        label: t('SenderDetails.emailAddress'),
        value: transaction.SenderEmailAddress,
        visible: transaction.SenderEmailAddress !== '',
      },
      {
        label: t('SenderDetails.address'),
        value: [transaction.SenderAddress1, transaction.SenderAddress2, transaction.SenderAddressProvince]
          .filter((item) => !!item)
          .join(', '),
        visible: !!transaction.SenderAddress1 || !!transaction.SenderAddress2 || !!transaction.SenderAddressProvince,
      },
      {
        label: t('SenderDetails.city'),
        value: transaction.SenderAddressCity,
        visible: transaction.SenderAddressCity !== '',
      },
      {
        label: t('SenderDetails.postalCode'),
        value: transaction.SenderAddressPostalCode,
        visible: !!transaction.SenderAddressPostalCode,
      },
      {
        label: t('SenderDetails.country'),
        value: transaction.SenderAddressCountry,
        visible: true,
      },
      {
        label: t('SenderDetails.purposeOfPayment'),
        value: transaction.SenderPurposeOfPayment,
        visible: transaction.SenderPurposeOfPayment !== '',
      },
      {
        label: t('SenderDetails.businessName'),
        value: transaction.SenderBusinessName,
        visible: transaction.SenderBusinessName !== '',
      },
      {
        label: t('SenderDetails.currency'),
        value: transaction.SenderCurrency,
        visible: !!transaction.SenderCurrency,
      },
    ];

    const senderDetailsProps: vp.TableProps = {
      name: 'sender-details',
      variant: 'pair',
      columns: {
        label: { header: t('SenderDetails.header') || '' },
        value: { header: '' },
      },
      rows: senderDetailsPairs.filter(({ visible }) => visible),
    };

    return senderDetailsProps;
  }, [transaction, clientAccountsEnabled, state]);

  const recipientDetailsProps = useMemo(() => {
    const recipientDetailsPairs: KeyValuePair[] = [
      {
        label: t('RecipientDetails.name'),
        value: (
          <Box jc="fs" ai="center" gap="sm">
            <Box>{`${transaction.RecipientFirstName} ${transaction.RecipientLastName}`}</Box>
          </Box>
        ),
        visible: !!transaction.RecipientFirstName,
      },
      {
        label: t('RecipientDetails.emailAddress'),
        value: transaction.RecipientEmailAddress,
        visible: transaction.RecipientEmailAddress !== '',
      },
      {
        label: t('RecipientDetails.address'),
        value: [transaction.RecipientAddress1, transaction.RecipientAddress2, transaction.RecipientAddressProvince]
          .filter((item) => !!item)
          .join(', '),
        visible:
          !!transaction.RecipientAddress1 || !!transaction.RecipientAddress2 || !!transaction.RecipientAddressProvince,
      },
      {
        label: t('RecipientDetails.city'),
        value: transaction.RecipientAddressCity,
        visible: transaction.RecipientAddressCity !== '',
      },
      {
        label: t('RecipientDetails.postalCode'),
        value: transaction.RecipientAddressPostalCode,
        visible: !!transaction.RecipientAddressPostalCode,
      },
      {
        label: t('RecipientDetails.country'),
        value: transaction.RecipientAddressCountry,
        visible: true,
      },
      {
        label: t('RecipientDetails.primaryContact'),
        value: transaction.RecipientPhone,
        visible: transaction.RecipientPhone !== '',
      },
      {
        label: t('RecipientDetails.businessName'),
        value: transaction.RecipientBusinessName,
        visible: transaction.RecipientBusinessName !== '',
      },
      {
        label: t('RecipientDetails.currency'),
        value: transaction.RecipientCurrency,
        visible: !!transaction.RecipientCurrency,
      },
    ];

    const recipientDetailsProps: vp.TableProps = {
      name: 'recipient-details',
      variant: 'pair',
      columns: {
        label: { header: t('RecipientDetails.header') || '' },
        value: { header: '' },
      },
      rows: recipientDetailsPairs.filter(({ visible }) => visible),
    };

    return recipientDetailsProps;
  }, [transaction]);

  const errorInfoProps = useMemo<vp.TableProps>(
    () => ({
      name: 'error-info',
      variant: 'pair',
      columns: {
        detailKey: { header: t('ErrorInfo.header') || '' },
        detailValue: { header: '' },
      },
      rows: [
        {
          label: t('ErrorInfo.failureDateTime'),
          value: transaction.TransactionDate,
        },
        {
          label: t('ErrorInfo.amount'),
          value: `$${formattedCurrency(transaction.Amount)}`,
        },
        {
          label: t('ErrorInfo.failureReason'),
          value: transaction.FailureReason,
        },
      ],
    }),
    [transaction],
  );

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...transactionDetailsProps} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('senderDetailsTitle') || ''} />
      </Box>
      <Table {...senderDetailsProps} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('recipientDetailsTitle') || ''} />
      </Box>
      <Table {...recipientDetailsProps} />
      {transaction.FailureReason && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...errorInfoProps} />
        </>
      )}
    </>
  );
};

export default CrossBorderWithdrawalInfo;
